/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { derivedDisposable } from '@vscode/observables';
import { obsView } from './obsView.js';
import { mapObject } from './utils.js';
import { getOrCreateViewModelContext } from './viewModel.js';

/** Collect unique _requiredContext from transformers */
function collectRequiredContexts(propsDesc) {
    const contexts = [];
    for (const t of Object.values(propsDesc)) {
        const ctx = t._requiredContext;
        if (ctx && !contexts.includes(ctx))
            contexts.push(ctx);
    }
    return contexts;
}
function viewWithModel(viewModelCtor, propsOrRender, maybeRender) {
    const props = typeof propsOrRender === 'function' ? {} : propsOrRender;
    const render = typeof propsOrRender === 'function' ? propsOrRender : maybeRender;
    const modelPropsDesc = '_props' in viewModelCtor ? viewModelCtor._props : {};
    const requiredContexts = collectRequiredContexts({ ...modelPropsDesc, ...props });
    // Always create the context so ProvideViewModel can work
    const viewModelContext = getOrCreateViewModelContext(viewModelCtor);
    const allContexts = [...requiredContexts, viewModelContext];
    return obsView('viewWithModel', (p, getContextValues) => {
        const contextValues = getContextValues();
        const providedModel = contextValues.get(viewModelContext);
        const readableModelProps = '_props' in viewModelCtor
            ? mapObject(viewModelCtor._props, (v, k) => v.create((r) => p.read(r)[k], contextValues.get(v._requiredContext)))
            : {};
        const model = providedModel
            ? { read: () => providedModel, dispose: () => { } }
            : derivedDisposable(reader => {
                const modelProps = mapObject(readableModelProps, (v) => v.read(reader));
                return new viewModelCtor(modelProps);
            });
        const readableProps = mapObject(props, (v, k) => v.create((r) => p.read(r)[k], contextValues.get(v._requiredContext)));
        return (reader) => {
            const m = model.read(reader);
            const propValues = mapObject(readableProps, (v) => v.read(reader));
            return render(reader, m, propValues);
        };
    }, allContexts.length > 0 ? allContexts : undefined);
}

export { viewWithModel };
//# sourceMappingURL=viewWithModel.js.map
